// ===========================================
// IMPORT PIPELINE (CSV) — a ClickUp list export becomes wholesale accounts
// ===========================================
// CPG founders usually track their pipeline in a ClickUp list (or a sheet that
// looks like one). This modal reads that export — paste or upload — sniffs the
// headers case-insensitively (Task Name/Account, Status/Stage, Notes, Email,
// Next step, Due date), shows the mapping for a human to confirm, then builds
// the pipeline through the SAME endpoints the Accounts page uses: create the
// customer, set their stage, log one activity with the notes. Existing
// accounts (matched by name) are left exactly as they are — an import never
// clobbers a pipeline you've been working.

import { useState } from 'react';
import * as XLSX from 'xlsx';
import { UploadCloud, X } from 'lucide-react';
import { wholesaleApi, hubApi } from '../../services/api';

const STAGE_LABELS: Record<string, string> = {
  lead: 'Lead', contacted: 'Contacted', sample_sent: 'Sample sent',
  negotiating: 'Negotiating', won: 'Won', lost: 'Lost',
};

// Common ClickUp status names → pipeline stages. Exact matches first, then
// keyword fallbacks for house styles ("CLOSED — WON 🎉"); anything else is a
// lead — the honest floor, never a guess upward.
const mapStatus = (raw: string): string => {
  const s = String(raw || '').toLowerCase().replace(/[^a-z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!s) return 'lead';
  const exact: Record<string, string> = {
    lead: 'lead', 'new': 'lead', 'new lead': 'lead', open: 'lead', 'to do': 'lead', todo: 'lead', backlog: 'lead', prospect: 'lead', cold: 'lead',
    contacted: 'contacted', 'in progress': 'contacted', 'reached out': 'contacted', 'follow up': 'contacted', followup: 'contacted', emailed: 'contacted', called: 'contacted', 'attempt to contact': 'contacted', 'in contact': 'contacted',
    'sample sent': 'sample_sent', 'samples sent': 'sample_sent', sample: 'sample_sent', samples: 'sample_sent', sampling: 'sample_sent', demo: 'sample_sent', tasting: 'sample_sent',
    negotiating: 'negotiating', negotiation: 'negotiating', proposal: 'negotiating', 'proposal sent': 'negotiating', 'quote sent': 'negotiating', pricing: 'negotiating', 'in review': 'negotiating', review: 'negotiating',
    won: 'won', 'closed won': 'won', complete: 'won', completed: 'won', done: 'won', active: 'won', customer: 'won', ordering: 'won',
    lost: 'lost', 'closed lost': 'lost', dead: 'lost', 'not interested': 'lost', declined: 'lost', churned: 'lost', passed: 'lost',
  };
  if (exact[s]) return exact[s];
  if (/\bwon\b/.test(s)) return 'won';
  if (/\b(lost|dead)\b/.test(s)) return 'lost';
  if (/sampl|demo|tast/.test(s)) return 'sample_sent';
  if (/negoti|proposal|quote|pricing/.test(s)) return 'negotiating';
  if (/contact|follow|email|call|outreach/.test(s)) return 'contacted';
  return 'lead';
};

type FieldKey = 'name' | 'status' | 'notes' | 'email' | 'nextStep' | 'dueDate';
const FIELDS: Array<{ key: FieldKey; label: string; hints: string[] }> = [
  { key: 'name', label: 'Account name', hints: ['task name', 'name', 'account', 'account name', 'company', 'customer', 'business', 'lead'] },
  { key: 'status', label: 'Stage / status', hints: ['status', 'stage', 'pipeline stage', 'pipeline'] },
  { key: 'notes', label: 'Notes', hints: ['notes', 'note', 'description', 'comments', 'comment', 'details'] },
  { key: 'email', label: 'Email', hints: ['email', 'e mail', 'email address', 'contact email', 'contact'] },
  { key: 'nextStep', label: 'Next step', hints: ['next step', 'next steps', 'next action', 'action', 'follow up'] },
  { key: 'dueDate', label: 'Due date', hints: ['due date', 'due', 'deadline', 'next step due', 'follow up date'] },
];

const normHeader = (h: string) => String(h || '').toLowerCase().replace(/[^a-z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim();

// Two passes: exact hint matches claim their column first, then containment
// mops up the unclaimed ones — so "Assignee name" never steals "Task Name"'s
// job. -1 = not in this file.
const sniffHeaders = (headers: string[]): Record<FieldKey, number> => {
  const normed = headers.map(normHeader);
  const claimed = new Set<number>();
  const out = { name: -1, status: -1, notes: -1, email: -1, nextStep: -1, dueDate: -1 } as Record<FieldKey, number>;
  for (const f of FIELDS) {
    const i = normed.findIndex((h, idx) => !claimed.has(idx) && f.hints.includes(h));
    if (i >= 0) { out[f.key] = i; claimed.add(i); }
  }
  for (const f of FIELDS) {
    if (out[f.key] >= 0) continue;
    const i = normed.findIndex((h, idx) => !claimed.has(idx) && h && f.hints.some((hint) => h.includes(hint)));
    if (i >= 0) { out[f.key] = i; claimed.add(i); }
  }
  return out;
};

// ClickUp exports dates as text or a 13-digit epoch — accept both, never guess.
const toIso = (v: string): string | undefined => {
  const t = String(v || '').trim();
  if (!t) return undefined;
  const d = /^\d{13}$/.test(t) ? new Date(Number(t)) : new Date(t);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
};

type Result = { created: number; skippedExisting: number; skippedNoName: number; staged: number; activities: number; failed: string[] };

export default function ImportPipelineModal({ customers, onClose, onDone, onError }: {
  customers: Array<{ id: string; name: string }>;
  onClose: () => void;
  onDone: (msg: string) => void;
  onError: (msg: string) => void;
}) {
  const [step, setStep] = useState<'input' | 'map' | 'result'>('input');
  const [pasted, setPasted] = useState('');
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<string[][]>([]);
  const [mapping, setMapping] = useState<Record<FieldKey, number>>({ name: -1, status: -1, notes: -1, email: -1, nextStep: -1, dueDate: -1 });
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<Result | null>(null);

  const load = (data: string | ArrayBuffer, type: 'string' | 'array') => {
    try {
      const wb = XLSX.read(data, { type, raw: true });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const grid = (XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false }) as any[][])
        .map((r) => (r || []).map((c) => (c == null ? '' : String(c).trim())));
      if (grid.length < 2) { onError('That file has a header row but no accounts under it.'); return; }
      const hdr = grid[0];
      const body = grid.slice(1).filter((r) => r.some((c) => c)).slice(0, 500);
      const sniffed = sniffHeaders(hdr);
      if (sniffed.name < 0) {
        onError("Couldn't find an account-name column (looked for Task Name, Name, Account, Company…). Check the headers and try again.");
        return;
      }
      setHeaders(hdr); setRows(body); setMapping(sniffed); setStep('map');
    } catch {
      onError("That didn't read as a CSV — export the ClickUp list as CSV and try again.");
    }
  };

  const runImport = async () => {
    setBusy(true);
    const existing = new Set(customers.map((c) => c.name.trim().toLowerCase()));
    const res: Result = { created: 0, skippedExisting: 0, skippedNoName: 0, staged: 0, activities: 0, failed: [] };
    const cell = (r: string[], k: FieldKey) => (mapping[k] >= 0 ? String(r[mapping[k]] || '').trim() : '');
    for (let i = 0; i < rows.length; i++) {
      setProgress(i + 1);
      const r = rows[i];
      const name = cell(r, 'name');
      if (!name) { res.skippedNoName++; continue; }
      if (existing.has(name.toLowerCase())) { res.skippedExisting++; continue; }
      try {
        const email = cell(r, 'email');
        const created = await wholesaleApi.createCustomer({ name, ...(email.includes('@') ? { email } : {}) });
        const id = created.data?.data?.id;
        if (!id) throw new Error('no id back');
        existing.add(name.toLowerCase());
        res.created++;
        // Stage: lead is already every new account's default — only a real
        // move up (or out) needs the PATCH.
        const stage = mapStatus(cell(r, 'status'));
        if (stage !== 'lead') {
          try { await hubApi.setCustomerStage(id, stage); res.staged++; } catch { res.failed.push(`${name}: stage not set`); }
        }
        const notes = cell(r, 'notes');
        const nextStep = cell(r, 'nextStep');
        const dueAt = toIso(cell(r, 'dueDate'));
        if (notes || nextStep) {
          try {
            await hubApi.addCustomerActivity(id, {
              note: notes || 'Imported from ClickUp',
              ...(nextStep ? { nextStep } : {}),
              ...(dueAt ? { dueAt } : {}),
            });
            res.activities++;
          } catch { res.failed.push(`${name}: activity not logged`); }
        }
      } catch {
        res.failed.push(`${name}: couldn't be created`);
      }
    }
    setBusy(false);
    setResult(res);
    setStep('result');
    onDone(`Pipeline imported — ${res.created} account${res.created === 1 ? '' : 's'} created, ${res.skippedExisting} already here.`);
  };

  const colName = (i: number) => (i >= 0 && headers[i] ? headers[i] : '—');
  const preview = rows.slice(0, 5);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 sm:p-5 space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="font-semibold text-slate-900">Import pipeline (CSV)</h2>
              <p className="text-xs text-slate-500 mt-0.5">
                A ClickUp list export (or any sheet with account + status columns) becomes wholesale accounts with their stage and notes. Accounts already here are left untouched.
              </p>
            </div>
            <button onClick={onClose} className="p-1.5 text-slate-400 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
          </div>

          {step === 'input' && (
            <div className="space-y-3">
              <label className="btn btn-secondary inline-flex items-center gap-2 cursor-pointer">
                <UploadCloud className="w-4 h-4" /> Upload the CSV
                <input type="file" accept=".csv,.tsv,.xlsx,.xls" className="hidden"
                  onChange={async (e) => { const f = e.target.files?.[0]; if (f) load(await f.arrayBuffer(), 'array'); e.target.value = ''; }} />
              </label>
              <p className="text-xs text-slate-400">…or paste the rows (header row included):</p>
              <textarea value={pasted} onChange={(e) => setPasted(e.target.value)}
                placeholder={'Task Name,Status,Notes,Email,Next step,Due date\nWhole Foods SoPac,Sample sent,Buyer liked the 8oz,…'}
                className="input w-full min-h-[120px] font-mono text-xs" />
              <div className="flex justify-end gap-2">
                <button onClick={onClose} className="btn btn-ghost">Cancel</button>
                <button onClick={() => load(pasted, 'string')} disabled={!pasted.trim()} className="btn btn-primary">Read it</button>
              </div>
            </div>
          )}

          {step === 'map' && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">{rows.length} row{rows.length === 1 ? '' : 's'} found. Check the column mapping — then import.</p>
              <div className="grid sm:grid-cols-2 gap-2">
                {FIELDS.map((f) => (
                  <label key={f.key} className="text-xs text-slate-500 space-y-1">
                    <span>{f.label}{f.key === 'name' ? ' *' : ''}</span>
                    <select value={mapping[f.key]} onChange={(e) => setMapping({ ...mapping, [f.key]: parseInt(e.target.value, 10) })} className="input w-full text-sm">
                      <option value={-1}>Not in this file</option>
                      {headers.map((h, i) => <option key={i} value={i}>{h || `Column ${i + 1}`}</option>)}
                    </select>
                  </label>
                ))}
              </div>
              <div className="rounded-xl border border-slate-200 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-[10px] uppercase text-slate-400 border-b border-slate-100 bg-slate-50">
                      <th className="px-2 py-1.5 text-left">Account ({colName(mapping.name)})</th>
                      <th className="px-2 py-1.5 text-left">Stage ({colName(mapping.status)})</th>
                      <th className="px-2 py-1.5 text-left">Email</th>
                      <th className="px-2 py-1.5 text-left">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((r, i) => {
                      const get = (k: FieldKey) => (mapping[k] >= 0 ? String(r[mapping[k]] || '') : '');
                      const name = get('name');
                      const exists = customers.some((c) => c.name.trim().toLowerCase() === name.trim().toLowerCase());
                      return (
                        <tr key={i} className="border-b border-slate-50">
                          <td className="px-2 py-1.5 whitespace-nowrap max-w-[160px] truncate">
                            {name || <span className="text-slate-300">no name — skipped</span>}
                            {exists && <span className="ml-1 text-[9px] text-slate-400 bg-slate-100 px-1 py-0.5 rounded">already here</span>}
                          </td>
                          <td className="px-2 py-1.5 whitespace-nowrap">
                            {get('status') ? <>{get('status')} <span className="text-slate-400">→ {STAGE_LABELS[mapStatus(get('status'))]}</span></> : <span className="text-slate-300">→ Lead</span>}
                          </td>
                          <td className="px-2 py-1.5 whitespace-nowrap max-w-[140px] truncate">{get('email') || <span className="text-slate-300">—</span>}</td>
                          <td className="px-2 py-1.5 max-w-[180px] truncate">{get('notes') || <span className="text-slate-300">—</span>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {rows.length > preview.length && <p className="px-2 py-1.5 text-[10px] text-slate-400">…and {rows.length - preview.length} more</p>}
              </div>
              <div className="flex justify-between gap-2">
                <button onClick={() => setStep('input')} className="btn btn-ghost" disabled={busy}>Back</button>
                <div className="flex gap-2">
                  <button onClick={onClose} className="btn btn-ghost" disabled={busy}>Cancel</button>
                  <button onClick={() => void runImport()} disabled={busy || mapping.name < 0} className="btn btn-primary">
                    {busy ? `Importing… ${progress}/${rows.length}` : `Import ${rows.length} row${rows.length === 1 ? '' : 's'}`}
                  </button>
                </div>
              </div>
            </div>
          )}

          {step === 'result' && result && (
            <div className="space-y-3">
              <div className="rounded-xl bg-slate-50 p-3 text-sm text-slate-700 space-y-1">
                <p><b>{result.created}</b> account{result.created === 1 ? '' : 's'} created · <b>{result.staged}</b> staged beyond Lead · <b>{result.activities}</b> note{result.activities === 1 ? '' : 's'} logged</p>
                <p className="text-xs text-slate-500">
                  {result.skippedExisting > 0 && <>{result.skippedExisting} already here (left untouched). </>}
                  {result.skippedNoName > 0 && <>{result.skippedNoName} row{result.skippedNoName === 1 ? '' : 's'} had no name. </>}
                  New accounts without a recognized status start as Leads.
                </p>
              </div>
              {result.failed.length > 0 && (
                <div className="rounded-xl bg-amber-50 border border-amber-100 p-3 text-xs text-amber-800">
                  <p className="font-medium mb-1">{result.failed.length} didn't fully land:</p>
                  {result.failed.slice(0, 8).map((f, i) => <p key={i}>{f}</p>)}
                  {result.failed.length > 8 && <p>…and {result.failed.length - 8} more</p>}
                </div>
              )}
              <div className="flex justify-end">
                <button onClick={onClose} className="btn btn-primary">Done</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
